// Sidebar footer: turns meaning search on or off, and shows indexing progress
import { useState } from "react"
import { Button } from "@/components/ui/button"
import { Switch } from "@/components/ui/switch"
import { cn } from "@/lib/utils"
import { AlertTriangle, Loader2, Sparkles } from "lucide-react"
import type { SearchIndex } from "../search/useSearchIndex"

const HELP =
  "Lets CMS Navigator find content by meaning as well as by its exact words, in every language. "
  + "Content is embedded with your team's OpenAI key and kept up to date as you edit."

export function SearchIndexStatus({ index }: { index: SearchIndex }) {
  const [confirmOff, setConfirmOff] = useState(false)
  const { state, progress, error, canManage } = index
  if (state === "checking" || (!canManage && state !== "on")) return null

  if (confirmOff) {
    return (
      <div className="flex shrink-0 items-center gap-2 border-t px-3 py-2 text-xs">
        <span className="flex-1 text-muted-foreground">Turn off and delete the stored vectors?</span>
        <Button size="sm" variant="destructive" className="h-6 px-2 text-xs" onClick={() => { setConfirmOff(false); void index.turnOff() }}>Turn off</Button>
        <Button size="sm" variant="ghost" className="h-6 px-2 text-xs" onClick={() => setConfirmOff(false)}>Cancel</Button>
      </div>
    )
  }

  const detail = state === "unavailable" ? "Not set up"
    : progress ? `Indexing ${progress.done}/${progress.total}`
      : error ? "Needs attention"
        : state === "on" ? (index.storedCount ? `Up to date · ${index.storedCount.toLocaleString()} sections` : "Up to date") : "Off"

  return (
    <div className="flex shrink-0 items-center gap-2 border-t px-3 py-2 text-xs" title={error ?? HELP}>
      <Sparkles className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
      <span className="flex-1 truncate">Search by meaning</span>
      <span className={cn("flex items-center gap-1 text-muted-foreground", error && "text-destructive")}>
        {progress && <Loader2 className="h-3 w-3 animate-spin" />}
        {error && !progress && <AlertTriangle className="h-3 w-3" />}
        {detail}
      </span>
      {canManage && state !== "unavailable" && (
        <Switch
          size="sm"
          checked={state === "on"}
          onCheckedChange={on => (on ? index.turnOn() : setConfirmOff(true))}
          aria-label="Search by meaning"
        />
      )}
    </div>
  )
}
